import React, { ReactNode } from 'react';
import Skeleton from 'react-loading-skeleton';
import dayjs from 'dayjs';
import styled, { css } from 'styled-components';
import { lineHeight, theme, toRem } from 'design';
import { Appearance } from 'design/types';
import { ButtonText } from '../Button';
import { Icon, IconTypes } from '../Icon';
import { H3 } from '../Typography';

interface ICardRowProps {
  marginBottom?: 'large' | 'small';
}

export const CardRow = styled.section<ICardRowProps>`
  position: relative;
  z-index: ${theme.zIndex.contentHeader};
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(${toRem(250)}, 1fr));
  grid-gap: ${toRem(theme.space.md)};
  width: 100%;
  margin-bottom: ${toRem(theme.space.xl)};
  padding: 0 ${toRem(theme.space.xl)};

  @media (min-width: ${theme.breakpoint.small}px) {
    margin-bottom: ${toRem(theme.space['3xl'])};
    padding: 0 ${toRem(theme.space['3xl'])};
  }

  @media (min-width: ${theme.breakpoint.med}px) {
    grid-gap: ${toRem(theme.space.xl)};
    margin-top: ${toRem(-theme.card.minHeight.large / 2)};
  }

  ${({ marginBottom }) =>
    marginBottom === 'small' &&
    css`
      margin-bottom: ${toRem(theme.space.xl)};

      @media (min-width: ${theme.breakpoint.small}px) {
        margin-bottom: ${toRem(theme.space.xl)};
      }
    `}
`;

export const Card = styled.div<{ error?: boolean }>`
  position: relative;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  height: auto;
  min-height: ${toRem(theme.card.minHeight.small)};
  padding: ${toRem(theme.space.xl)};
  border-radius: ${toRem(11)};
  ${(p) => css`
    border: ${toRem(1)} solid ${p.error ? theme.color.danger[25] : theme.color.neutral[200]};
  `}

  background-color: ${theme.color.white};
  overflow: hidden;

  @media (min-width: ${theme.breakpoint.med}px) {
    min-height: ${toRem(theme.card.minHeight.large)};
  }

  &:focus {
    border-color: ${theme.color.neutral[400]};
    outline: none;
  }
`;

interface ICardIconProps {
  bgColor?: string;
  className?: string;
  icon: IconTypes;
  fill?: string;
  isLoading?: boolean;
}

const COLOR_DEFAULT = theme.color.neutral[900];

const CardIconLayout = styled.div<Partial<ICardIconProps>>`
  position: relative;
  flex-flow: column nowrap;
  justify-content: center;
  align-items: center;
  flex-grow: 0;
  flex-shrink: 0;
  width: ${toRem(33)};
  height: ${toRem(33)};
  margin-right: ${toRem(theme.space.xl)};
  border-radius: 50%;
  background-color: transparent;
  pointer-events: none;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background-color: ${({ bgColor }) => bgColor || COLOR_DEFAULT};
    opacity: 0.15;
  }

  svg {
    width: ${toRem(16.5)};
    height: ${toRem(16.5)};
  }

  @media (min-width: ${theme.breakpoint.med}px) {
    width: ${toRem(44)};
    height: ${toRem(44)};

    svg {
      width: ${toRem(22)};
      height: ${toRem(22)};
    }
  }

  ${({ bgColor }) =>
    bgColor &&
    css`
      background-color: ${bgColor};
    `}
`;

const CardIconLoading = styled(Skeleton)`
  && {
    width: ${toRem(33)};
    height: ${toRem(33)};
    margin-right: ${toRem(theme.space.xl)};
    border-radius: 50%;

    @media (min-width: ${theme.breakpoint.med}px) {
      width: ${toRem(44)};
      height: ${toRem(44)};
    }
  }
`;

export const CardIcon: React.FC<ICardIconProps> = ({ bgColor, className, icon, fill = theme.color.neutral[800], isLoading }) =>
  isLoading ? (
    <CardIconLoading />
  ) : (
    <CardIconLayout className={className} bgColor={bgColor} aria-hidden="true">
      <Icon icon={icon} fill={fill} />
    </CardIconLayout>
  );

interface ICardIconStatusProps {
  color: Appearance;
  icon: IconTypes;
  inverse?: boolean;
  isLoading?: boolean;
}

export const CardIconStatus: React.FC<ICardIconStatusProps> = ({ color, icon, isLoading, inverse }) => {
  let bgColor = inverse ? theme.color.neutral[400] : theme.color.neutral[100];
  let fill = inverse ? theme.color.white : theme.color.neutral[400];

  if (color === Appearance.PRIMARY) {
    bgColor = inverse ? theme.color.primary[500] : theme.color.primary[25];
    fill = inverse ? theme.color.white : theme.color.primary[500];
  }

  if (color === Appearance.NEUTRAL) {
    bgColor = inverse ? theme.color.neutral[400] : theme.color.neutral[100];
    fill = inverse ? theme.color.white : theme.color.neutral[500];
  }

  if (color === Appearance.SUCCESS) {
    bgColor = inverse ? theme.color.success[50] : theme.color.success[25];
    fill = inverse ? theme.color.white : theme.color.success[50];
  }

  if (color === Appearance.WARNING) {
    bgColor = inverse ? theme.color.warning[50] : theme.color.warning[25];
    fill = inverse ? theme.color.white : theme.color.warning[50];
  }

  if (color === Appearance.DANGER) {
    bgColor = inverse ? theme.color.danger[50] : theme.color.danger[25];
    fill = inverse ? theme.color.white : theme.color.danger[50];
  }

  return <CardIcon bgColor={bgColor} icon={icon} fill={fill} isLoading={isLoading} />;
};

export const CardContent = styled.div`
  flex-flow: column nowrap;
  justify-content: center;
  align-items: flex-start;
  width: auto;
  height: auto;
  gap: ${toRem(theme.space.sm)};
`;

const CardLabelLayout = styled.label`
  font-family: ${theme.font.family.euclid};
  font-weight: ${theme.font.weight.medium};
  font-size: ${toRem(theme.font.size.sm)};
  line-height: ${lineHeight(theme.font.size.sm)};
  color: ${theme.color.neutral[500]};
`;

interface ICardElementProps {
  className?: string;
  children?: ReactNode;
  isLoading?: boolean;
}

const CardValueLayout = styled(H3)`
  display: flex;
  flex-flow: row nowrap;
  color: ${theme.color.neutral[900]};
  word-break: break-word;

  em {
    font-weight: ${theme.font.weight.regular};
    font-style: normal;
  }
`;

export const CardLabel = ({ className, children, isLoading }: ICardElementProps) =>
  isLoading ? (
    <Skeleton width={100} />
  ) : (
    <CardLabelLayout className={className} data-cy="card-label">
      {children}
    </CardLabelLayout>
  );

export const CardValue = ({ className, children, isLoading }: ICardElementProps) =>
  isLoading ? (
    <Skeleton width={75} />
  ) : (
    <CardValueLayout className={className} data-cy="card-value">
      {children}
    </CardValueLayout>
  );

export const getExpirationStatus = (expDate: string) => {
  const today = dayjs(new Date());
  const d = dayjs(dayjs(expDate, 'MM/YY')).diff(today, 'month');

  if (d < 1) {
    return d < 0 ? 'Expired' : 'Expires soon';
  }

  return '';
};

interface CardDetailProps {
  fontStyle?: 'normal' | 'italic';
}

const CardDetailLayout = styled.span<CardDetailProps>`
  font-family: ${theme.font.family.euclid};
  font-weight: ${theme.font.weight.regular};
  font-size: ${toRem(theme.font.size.xxs)};
  line-height: ${lineHeight(theme.font.size.xxs)};
  color: ${theme.color.neutral[900]};

  ${({ fontStyle }) =>
    fontStyle === 'italic' &&
    css`
      font-style: italic;
    `}
`;

export const CardDetail = ({ className, children, isLoading }: ICardElementProps) =>
  isLoading ? (
    <Skeleton width={65} />
  ) : (
    <CardDetailLayout className={className} data-cy="card-detail">
      {children}
    </CardDetailLayout>
  );

export const CardAction = styled(ButtonText)`
  margin-top: ${toRem(theme.space['2xs'])};
  font-size: ${toRem(theme.font.size.xxs)};
  line-height: ${lineHeight(theme.font.size.xxs)};
  text-decoration: none;
  color: ${theme.color.primary[500]};

  &:hover {
    color: ${theme.color.primary[600]};
  }

  &:active,
  &:visited {
    color: ${theme.color.primary[500]};
  }
`;
